import {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime,
  fromDate,
  getLocalTimeZone,
  toCalendarDateTime,
} from "@internationalized/date";
import {
  addHours,
  endOfDay,
  format,
  formatISO,
  isAfter,
  isBefore,
  isToday,
  isYesterday,
  parseISO,
  startOfDay,
} from "date-fns";
import { nb } from "date-fns/locale";
import { getCurrentCalendarDateTime } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/transformers";
import { Nullish } from "shared/utils/objectUtils";

export const getISODrivingFromToTimes = (inputDate: Date | null) => {
  const date = inputDate ?? new Date();
  const from = formatISO(addHours(startOfDay(date), 3));
  const to = formatISO(addHours(endOfDay(date), 3));

  return { from, to };
};
export const getDate = (inputDate: Date | null) => {
  const date = inputDate ?? new Date();
  return formatISO(date, { representation: "date" });
};

export const isBeforeNow = (time: ZonedDateTime) => {
  const now = fromDate(new Date(), getLocalTimeZone());
  return time.compare(now) < 0;
};
export const parseZonedTime = (time: string) =>
  fromDate(new Date(time), getLocalTimeZone());

export const toFormTime = (value: Date | null | undefined) =>
  value ? toCalendarDateTime(fromDate(value, getLocalTimeZone())) : undefined;

// Compare days, ignoring hh:mm:ss:xxxx
export const isBeforeDay = (date: Date, dateToCompare: Date) => {
  const dateDay = startOfDay(date);
  const dateToCompareDay = startOfDay(dateToCompare);
  return isBefore(dateDay, dateToCompareDay);
};

// Compare days, ignoring hh:mm:ss:xxxx
export const isAfterDay = (date: Date, dateToCompare: Date) => {
  const dateDay = startOfDay(date);
  const dateToCompareDay = startOfDay(dateToCompare);
  return isAfter(dateDay, dateToCompareDay);
};

export const startOfDayCalendarDateTime = (
  date: CalendarDate | CalendarDateTime | ZonedDateTime,
) => new CalendarDateTime(date.year, date.month, date.day, 0, 0);

export const startOfTodayCalendarDateTime = () =>
  startOfDayCalendarDateTime(getCurrentCalendarDateTime());

export const endOfDayCalendarDateTime = (
  date: CalendarDate | CalendarDateTime | ZonedDateTime,
) => new CalendarDateTime(date.year, date.month, date.day, 23, 59);

export const endOfTodayCalendarDateTime = () =>
  endOfDayCalendarDateTime(getCurrentCalendarDateTime());

export const formatTrainDate = (trainDate: string | Date) => {
  try {
    return format(new Date(trainDate), "dd.MM");
  } catch {
    return "Feil datoformat";
  }
};

export const formatDayDateString = (dateString: Nullish<string>) => {
  if (dateString === null || !dateString) return "\u00a0";

  try {
    const date = parseISO(dateString);
    if (isToday(date)) return format(date, "HH:mm");
    if (isYesterday(date)) return `i går ${format(date, "HH:mm")}`;
    return format(date, "d. MMM HH:mm", { locale: nb });
  } catch {
    return "Feil datoformat";
  }
};

export const formatDateString = (
  dateString: string,
  formatString: string,
  failoverText = "",
  suffix = null,
) => {
  try {
    return `${format(parseISO(dateString), formatString)}${suffix || ""}`;
  } catch {
    return failoverText;
  }
};

export const formatTimeString = (timeString: string | Date | null) =>
  timeString ? format(new Date(timeString), "HH:mm") : null;

export const formatDateTimeString = (timeString: string | Date | null) =>
  timeString ? format(new Date(timeString), "HH:mm (dd.MM.yy)") : null;

export function compareDateStringAsc(a: string, b: string) {
  return new Date(b).valueOf() - new Date(a).valueOf();
}

export const formatMeasureCardDateTimeString = (dt: Date | null) => {
  if (!dt) return null;

  const dtFormat = isToday(dt) ? "HH:mm" : "HH:mm (dd.MM)";

  return format(dt, dtFormat);
};
